import React from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import "./LoginForm.css";
import Countries from "../../Countries";
import { Formik } from "formik";
import * as yup from "yup";
import { isPasswordStrongEnough } from '../../../utils/password';

// https://jaredpalmer.com/formik/docs/guides/form-submission

const requiredFieldMsg = "This field is required."

const schema = yup.object({
  password1: yup.string().required(requiredFieldMsg),
  password2: yup.string().required(requiredFieldMsg),
  firstName: yup.string().required(requiredFieldMsg),
  surname: yup.string().required(requiredFieldMsg),
  dobDay: yup.string().required(requiredFieldMsg),
  dobMonth: yup.string().required(requiredFieldMsg),
  dobYear: yup.string().required(requiredFieldMsg),
  passportNumber: yup.string().required(requiredFieldMsg),
  issuingCountry: yup.string().required(requiredFieldMsg),
  doeDay: yup.string().required(requiredFieldMsg),
  doeMonth: yup.string().required(requiredFieldMsg),
  doeYear: yup.string().required(requiredFieldMsg),
  personalNumber: yup.string()
});

/*
 * Helper Functions to populate the DOM
 */
function range(start, end) {
  var foo = [];
  for (var i = start; i <= end; i++) {
    foo.push(i);
  }
  return foo;
}

const generateOptions = (start, end) => {
  const res = [<option key={"empty"}></option>];
  return res.concat(
    range(start, end).map(n => {
      if (n < 10) {
        return <option key={n} value={"0" + n}>{n}</option>
      }
      return <option key={n}>{n}</option>;
    })
  );
};

const generateCountryOptions = countries => {
  const res = [<option key={"empty"}></option>];
  Object.entries(countries).forEach(([key, value]) => {
    res.push(
      <option key={key} value={key}>
        {value}
      </option>
    );
  });
  return res;
};



function LoginForm(props) {
  return (
    <Formik
      validationSchema={schema}
      validate={values => {
        const errors = {};
        // TODO in V2, show errors if dropdowns are left blank
        // if (!values.dobMonth || values.dobMonth == "") {
        //   errors.dobMonth = "Required";
        // }
        // console.log(errors);

        // validate if password meets safety criteria
        // minimum score of 4 to consider password safe
        if (values.password1 && !isPasswordStrongEnough(values.password1)) {
          errors.password1 = "Password does not meet security requirements."
        }

        // passwords must match
        if (values.password2 && values.password2 !== values.password1) {
          errors.password2 = "Passwords do not match."
        }

        return errors;
      }}
      // Useful for debugging
      // onSubmit={values => {
      //   alert(JSON.stringify(values, null, 2));
      // }}
      onSubmit={values => {
        props.handleSubmit(values);
      }}
      initialValues={{
        password1: "",
        password2: "",
        firstName: "",
        surname: "",
        passportNumber: "",
        personalNumber: "",
        issuingCountry: "",
        dobDay: "",
        dobMonth: "",
        dobYear: "",
        doeDay: "",
        doeMonth: "",
        doeYear: ""
      }}
    >
      {({
        handleSubmit,
        handleChange,
        // handleBlur,
        values,
        touched,
        // isValid,
        errors
      }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Row>
            <Col>
              <Form.Group controlId="validationCustomPassword1">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  name="password1"
                  value={values.password1}
                  onChange={handleChange}
                  isValid={touched.password1 && !errors.password1}
                  isInvalid={touched.password1 && !!errors.password1}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.password1}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Must be at least 8 characters, have at least one number, one uppercase letter, one lowercase letter, and one special character.
                </Form.Text>
              </Form.Group>
            </Col>
          </Form.Row>
          <Form.Row>
            <Col>
              <Form.Group controlId="validationCustomPassword2">
                <Form.Label>Retype Password</Form.Label>
                <Form.Control
                  type="password"
                  name="password2"
                  value={values.password2}
                  onChange={handleChange}
                  isValid={touched.password2 && !errors.password2}
                  isInvalid={touched.password2 && !!errors.password2}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.password2}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Form.Row>
          <Form.Row>
            <Form.Group as={Col} controlId="validationFormik01">
              <Form.Label>First name</Form.Label>
              <Form.Control
                type="text"
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                isValid={touched.firstName && !errors.firstName}
                isInvalid={touched.firstName && !!errors.firstName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstName}
              </Form.Control.Feedback>
            </Form.Group>
          </Form.Row>
          <Form.Row>
            <Form.Group as={Col} controlId="validationFormik02">
              <Form.Label>Surname</Form.Label>
              <Form.Control
                type="text"
                name="surname"
                value={values.surname}
                onChange={handleChange}
                isValid={touched.surname && !errors.surname}
                isInvalid={touched.surname && !!errors.surname}
              />
              <Form.Control.Feedback type="invalid">
                {errors.surname}
              </Form.Control.Feedback>
            </Form.Group>
          </Form.Row>
          <Form.Row>
            <Col>
              <Form.Label>Date of birth</Form.Label>
            </Col>
          </Form.Row>
          <Form.Row className="no-group">
            <Col>
              <Form.Control
                as="select"
                name="dobDay"
                onChange={handleChange}
                isInvalid={touched.dobDay && !!errors.dobDay}
                isValid={touched.dobDay && !errors.dobDay}
                feedback={errors.dobDay}
              >
                {generateOptions(1, 31)}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                  {errors.dobDay}
              </Form.Control.Feedback>
            </Col>

            <Col>
              <Form.Control
                as="select"
                name="dobMonth"
                onChange={handleChange}
                isInvalid={touched.dobMonth && !!errors.dobMonth}
                isValid={touched.dobMonth && !errors.dobMonth}
                feedback={errors.dobMonth}
              >
                <option key="0" value=""></option>
                <option key="1" value="01">
                  Jan
                </option>
                <option key="2" value="02">
                  Feb
                </option>
                <option key="3" value="03">
                  March
                </option>
                <option key="4" value="04">
                  April
                </option>
                <option key="5" value="05">
                  May
                </option>
                <option key="6" value="06">
                  June
                </option>
                <option key="7" value="07">
                  July
                </option>
                <option key="8" value="08">
                  Aug
                </option>
                <option key="9" value="09">
                  Sept
                </option>
                <option key="10" value="10">
                  Oct
                </option>
                <option key="11" value="11">
                  Nov
                </option>
                <option key="12" value="12">
                  Dec
                </option>
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.dobMonth}
              </Form.Control.Feedback>
            </Col>
            <Col>
              <Form.Control
                as="select"
                name="dobYear"
                onChange={handleChange}
                isInvalid={touched.dobYear && !!errors.dobYear}
                isValid={touched.dobYear && !errors.dobYear}
                feedback={errors.dobYear}
              >
                {generateOptions(1930, new Date().getFullYear() - 18)}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.dobYear}
              </Form.Control.Feedback>
            </Col>
          </Form.Row>
          <Form.Row>
            <Col>
              <Form.Group controlId="validationCustom2">
                <Form.Label>Passport number</Form.Label>
                <Form.Control
                  type="text"
                  name="passportNumber"
                  value={values.passportNumber}
                  onChange={handleChange}
                  isValid={touched.passportNumber && !errors.passportNumber}
                  isInvalid={touched.passportNumber && !!errors.passportNumber}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.passportNumber}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Form.Row>
          <Form.Row className="no-group">
            <Col>
              <Form.Label>Issuing country</Form.Label>
              <Form.Control
                as="select"
                name="issuingCountry"
                onChange={handleChange}
                isInvalid={touched.issuingCountry && !!errors.issuingCountry}
                isValid={touched.issuingCountry && !errors.issuingCountry}
                feedback={errors.issuingCountry}
              >
                {generateCountryOptions(Countries)}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                  {errors.issuingCountry}
              </Form.Control.Feedback>
            </Col>
          </Form.Row>

          <Form.Row>
            <Col>
              <Form.Label>Date of expiry</Form.Label>
            </Col>
          </Form.Row>
          <Form.Row className="no-group">
            <Col>
              <Form.Control
                as="select"
                name="doeDay"
                onChange={handleChange}
                isInvalid={touched.doeDay && !!errors.doeDay}
                isValid={touched.doeDay && !errors.doeDay}
                feedback={errors.doeDay}
              >
                {generateOptions(1, 31)}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.doeDay}
              </Form.Control.Feedback>
            </Col>

            <Col>
              <Form.Control
                as="select"
                name="doeMonth"
                onChange={handleChange}
                isInvalid={touched.doeMonth && !!errors.doeMonth}
                isValid={touched.doeMonth && !errors.doeMonth}
                feedback={errors.doeMonth}
              >
                <option key="0" value=""></option>
                <option key="1" value="01">
                  Jan
                </option>
                <option key="2" value="02">
                  Feb
                </option>
                <option key="3" value="03">
                  March
                </option>
                <option key="4" value="04">
                  April
                </option>
                <option key="5" value="05">
                  May
                </option>
                <option key="6" value="06">
                  June
                </option>
                <option key="7" value="07">
                  July
                </option>
                <option key="8" value="08">
                  Aug
                </option>
                <option key="9" value="09">
                  Sept
                </option>
                <option key="10" value="10">
                  Oct
                </option>
                <option key="11" value="11">
                  Nov
                </option>
                <option key="12" value="12">
                  Dec
                </option>
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.doeMonth}
              </Form.Control.Feedback>
            </Col>
            <Col>
              <Form.Control
                as="select"
                name="doeYear"
                onChange={handleChange}
                isInvalid={touched.doeYear && !!errors.doeYear}
                isValid={touched.doeYear && !errors.doeYear}
                feedback={errors.doeYear}
              >
                {generateOptions(
                  new Date().getFullYear(),
                  new Date().getFullYear() + 20
                )}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.doeYear}
              </Form.Control.Feedback>
            </Col>
          </Form.Row>
          <Form.Row>
            <Col>
              <Form.Group controlId="validationCustom03">
                <Form.Label>Personal number (optional)</Form.Label>
                <Form.Control
                  type="text"
                  name="personalNumber"
                  value={values.personalNumber}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Form.Row>
          <Button disabled={props.disabled} type="submit">Submit form</Button>
        </Form>
      )}
    </Formik>
  );
}

export default LoginForm;
